import { defineStore } from 'pinia';
import { getExpected, getPaid, isPaid } from '@/composables/useAppointmentCalculations';
import { useUserStore } from '@/stores/user';
import { collection, onSnapshot, where, query } from 'firebase/firestore';
import { firestore } from '@/firebase';

function monthKeysBetween(minTs, maxTs) {
  const min = Number(minTs);
  const max = Number(maxTs);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  const keys = [];
  let cursor = new Date(min);
  cursor = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const end = new Date(max);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cursor <= endMonth) {
    keys.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  return keys;
}

export const useAppointmentStore = defineStore('appointment', {
  state: () => ({
    appointments: {},
    unsubs: {},
    loadingMonths: [],
  }),
  persist: { paths: ['appointments'] },
  getters: {
    getExpected: () => getExpected,
    getPaid: () => getPaid,
    isPaid: () => isPaid,
    isAppointmentsLoadingForRange: (state) => (minTs, maxTs) => {
      const keys = monthKeysBetween(minTs, maxTs);
      return keys.some((key) => state.loadingMonths.includes(key));
    },
  },
  actions: {
    upsert({ id, value }) {
      this.appointments = { ...this.appointments, [id]: value };
    },
    remove(id) {
      const appointments = { ...this.appointments };
      delete appointments[id];
      this.appointments = appointments;
    },
    clearUnsubs() {
      for (const [, unsub] of Object.entries(this.unsubs)) {
        if (typeof unsub === 'function') unsub();
      }
      this.unsubs = {};
    },
    clear() {
      this.clearUnsubs();
      this.appointments = {};
    },
    /**
     * Registers a real-time listener for appointments in a single past month.
     * Used when the user navigates to a month outside the primary window (2 months ago to future).
     * On "removed" we only remove from store if the doc's date is still in range (actual delete);
     * otherwise the doc may have moved to another range (date changed).
     */
    registerMonthSnapshot(d) {
      const year = d.getFullYear();
      const month = d.getMonth();
      const monthStart = new Date(year, month, 1).getTime();
      const monthEnd = new Date(year, month + 1, 1).getTime();
      const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

      if (this.unsubs[monthKey]) return;

      if (!this.loadingMonths.includes(monthKey)) {
        this.loadingMonths = [...this.loadingMonths, monthKey];
      }

      const userStore = useUserStore();
      let monthQuery = query(collection(firestore, 'appointments'), where('date', '>=', monthStart), where('date', '<', monthEnd));
      if (!userStore.hasPermission('appointments:manage')) {
        monthQuery = query(monthQuery, where('lash', '==', userStore.userId));
      }

      const unsub = onSnapshot(monthQuery, (snapshot) => {
        this.loadingMonths = this.loadingMonths.filter((k) => k !== monthKey);
        snapshot.docChanges().forEach((element) => {
          const id = element.doc.id;
          const elementData = { ...element.doc.data(), id };

          if (element.type === 'added' || element.type === 'modified') {
            this.upsert({ id, value: elementData });
            return;
          }

          if (element.type === 'removed') {
            const current = this.appointments[id];
            const stillInRange =
              current &&
              typeof current.date === 'number' &&
              current.date >= monthStart &&
              current.date < monthEnd;
            if (stillInRange) {
              this.remove(id);
            }
          }
        });
      },
      (error) => {
        this.loadingMonths = this.loadingMonths.filter((k) => k !== monthKey);
        console.error('Appointment - Error registering snapshot', error);
      });

      this.unsubs[monthKey] = unsub;
    },
    ensureMonthsForRange(minTs, maxTs) {
      const keys = monthKeysBetween(minTs, maxTs);
      for (const key of keys) {
        const [y, m] = key.split('-').map(Number);
        this.registerMonthSnapshot(new Date(y, m - 1, 1));
      }
    },
  }
});
